import { firstValueFrom } from 'rxjs';
import { ProdutoPromocional } from '../models/produtos/produto-promocional';
import { ProdutoDesconto } from '../models/produtos/produto-desconto';

export function precoNegociadoSemFator(item: ProdutoPromocional, prazo: any, config: any): number {
  // O campo valor é o unitário digitado; o valor com fator fica em valor_fator_financeiro_produto.
  const valor = Number(item.valor);
  if (!Number.isFinite(valor) || valor <= 0) throw new Error('Preço negociado inválido.');
  return Number(valor.toFixed(4));
}

/** O preço-base retornado pela SP é sem fator. O desconto comercial também. */
export function aplicarDescontoPrazo(item: ProdutoPromocional, desconto: ProdutoDesconto): void {
  if (!(desconto?.preco_base > 0) || !Number.isFinite(desconto.desconto_concedido) || desconto.desconto_concedido < 0)
    throw new Error('Não foi possível determinar o preço-base de ' + item.name);
  item.preco_base_api = desconto.preco_base;
  item.valor_tabela = desconto.preco_base;
  item.porcentagem_desconto = desconto.desconto_concedido;
  item.porcentagem_desconto_temporaria = desconto.desconto_concedido || null;
  item.desconto_digitado = desconto.desconto_concedido;
  item.desconto_concedido_api = desconto.desconto_concedido;
  item.descritivo_desconto = desconto.descritivo_desconto;
  item.desconto_validado_por_prazo = true;
  item.base_desconto_referencia = desconto.preco_base;
  if (item.estado_preco) item.estado_preco = { ...item.estado_preco, baseDesconto: desconto.preco_base,
    precoBrutoSemFator: desconto.preco_base,
    precoFinal: item.valor, versao: item.estado_preco.versao + 1 };
}

export function copiarCondicoesPrazo(destino: ProdutoPromocional, origem: ProdutoPromocional): void {
  if (!origem?.desconto_validado_por_prazo || !destino) return;
  const campos = ['desconto_validado_por_prazo', 'requer_aprovacao_prazo', 'valor_validado_prazo',
    'preco_base_api', 'desconto_concedido_api', 'descritivo_desconto', 'porcentagem_desconto',
    'estado_preco', 'base_desconto_referencia', 'cd_tabela_preco', 'id_tabela_especial',
    'idprodutotabelaespecial', 'qtde_minima_tabela', 'preco_tabela_travado',
    'preco_tabela_travado_label', 'preco_tabela_especial_sem_fator', 'origem_preco',
    'bypass_desconto_maximo', 'ignorar_pontuacao_por_tabela', 'price_promotional', 'promocao'];
  campos.forEach(campo => destino[campo] = campo === 'estado_preco' && origem[campo]
    ? { ...origem[campo] } : origem[campo]);
}

/** Consulta todos os itens antes de trocar o prazo: erro não deixa o pedido parcialmente atualizado. */
export async function revalidarDescontoAoTrocarPrazo(tela: any, idPrazo: number): Promise<void> {
  if (tela.revalidandoPrazo) return;
  const prazo = tela.prazosPagamentoOriginal.find((p: any) => p.idprazopagamento === idPrazo);
  if (!prazo || !tela.clienteSelecionado) {
    tela.toastr.warning('', 'Selecione um prazo válido.'); return;
  }
  tela.revalidandoPrazo = true;
  const clienteId = tela.clienteSelecionado.id;
  const vendedorId = tela.vendedorSelecionado?.id;
  const prazoAnterior = tela.prazoPagamentoSelecionado?.idprazopagamento;
  try {
    const medio = await firstValueFrom(tela.prazoMedioService.getByIdPrazoPagamento(idPrazo));
    if (!medio) throw new Error('Não foi possível consultar o prazo médio.');
    const itens: ProdutoPromocional[] = [];
    const visitar = (lista: ProdutoPromocional[]) => (lista || []).forEach(p => {
      if (p.id_campanha != null && p.produtos_vinculados?.length) visitar(p.produtos_vinculados);
      else if (!p.bonificacao && p.valor > 0 && (p.qtde > 0 || p.qtde_bonificada > 0) && !itens.includes(p)) itens.push(p);
    });
    visitar(tela.produtosOriginal); visitar(tela.produtosAdicionados); visitar(tela.produtos);
    const consultas = new Map<string, Promise<any>>();
    const atualizacoes = await Promise.all(itens.map(async item => {
      const anterior = { ...item };
      const bruto = precoNegociadoSemFator(anterior, tela.prazoPagamentoSelecionado, tela.configuracao);
      const quantidade = Number(item.qtde || item.qtde_bonificada);
      const chave = [item.id, item.id_lote || 0, quantidade, bruto].join(':');
      if (!consultas.has(chave)) consultas.set(chave, Promise.all([
        firstValueFrom(tela.produtoService.getDescontoByProduto(tela.clienteSelecionado.id, item.id, item.valor,
          quantidade, tela.vendedorSelecionado.id, item.id_lote, 0, idPrazo, bruto, true)),
        firstValueFrom(tela.produtoService.getTabelaByProduto(tela.clienteSelecionado.id, tela.vendedorSelecionado.id,
          item.id, item.id_lote, idPrazo, true))
      ]));
      const [desconto, tabelas] = await consultas.get(chave);
      const elegiveis = tabelas.filter((t: any) => t.preco > 0 && quantidade >= Number(t.qtd_minima || 0)
        && (t.id_lote == null || Number(t.id_lote) === Number(item.id_lote)));
      const vinculo = item.cd_tabela_preco || item.id_tabela_especial || item.estado_preco?.idTabelaEspecial;
      const tabela = elegiveis.find((t: any) => t.idprodutotabelaespecial === vinculo
        && Math.abs(Number(t.preco) - Number(desconto.preco_base)) < 0.005)
        || elegiveis.find((t: any) => Math.abs(Number(t.preco) - Number(desconto.preco_base)) < 0.005);
      const novo: ProdutoPromocional = { ...item };
      const ignorar = tabela?.flag_naoaplicafatorpontuacaofin === true;
      // Este fluxo atende prazo antes dos itens. O fluxo financeiro existente continua no componente.
      novo.valor = item.valor;
      if (!Number.isFinite(novo.valor) || novo.valor <= 0) throw new Error('Preço inválido para o novo prazo.');
      novo.valor_total = Number((novo.valor * Number(item.qtde || 0)).toFixed(2));
      novo.valor_fator_financeiro_produto = novo.valor;
      novo.valor_fator_financeiro_total = novo.valor_total;
      novo.ignorar_pontuacao_por_tabela = ignorar;
      novo.price_promotional = elegiveis.length ? Math.min(...elegiveis.map((t: any) => Number(t.preco))) : null;
      novo.promocao = elegiveis.length > 0;
      if (!tabela || tabela.idprodutotabelaespecial !== vinculo) {
        novo.cd_tabela_preco = null; novo.id_tabela_especial = null; novo.idprodutotabelaespecial = null;
        novo.qtde_minima_tabela = null; novo.preco_tabela_travado = false; novo.preco_tabela_travado_label = null;
        novo.preco_tabela_especial_sem_fator = null; novo.origem_preco = 'manual'; novo.bypass_desconto_maximo = false;
        novo.preco_tabela_base_desconto = null;
        if (novo.estado_preco) novo.estado_preco = { ...novo.estado_preco, origem: 'manual', travado: false,
          bypassDescontoMaximo: false, idTabelaEspecial: null, qtdeMinimaTabela: null };
      }
      if (tabela) {
        novo.cd_tabela_preco = tabela.idprodutotabelaespecial;
        novo.id_tabela_especial = tabela.idprodutotabelaespecial;
        novo.qtde_minima_tabela = tabela.qtd_minima;
        novo.preco_tabela_especial_sem_fator = tabela.preco;
      }
      aplicarDescontoPrazo(novo, desconto);
      novo.erro_valor = false;
      novo.valor_validado_prazo = novo.valor;
      const limite = Math.max(Number(item.desc_maximo || 0),
        tela.configuracao.usa_conta_corrente_flex ? Number(item.cc_flex_desc_maximo || 0) : 0);
      novo.requer_aprovacao_prazo = limite > 0 && novo.porcentagem_desconto > limite;
      return { item, anterior, novo };
    }));
    if (tela.clienteSelecionado?.id !== clienteId || tela.vendedorSelecionado?.id !== vendedorId
      || tela.prazoPagamentoSelecionado?.idprazopagamento !== prazoAnterior
      || atualizacoes.some(({item, anterior}) => item.valor !== anterior.valor || item.qtde !== anterior.qtde || item.id_lote !== anterior.id_lote || item.qtde_bonificada !== anterior.qtde_bonificada))
      throw new Error('Um item foi alterado durante a consulta. Selecione o prazo novamente.');
    atualizacoes.forEach(({item, novo}) => Object.assign(item, novo));
    tela.prazoPagamentoSelecionado = prazo; tela.prazoMedio = medio;
    tela.prazoMedioDesconto = null; tela.prazoPagamentoInvalido = false;
    atualizacoes.forEach(({item}) => {
      // A fórmula de pontuação existente é aplicada às novas condições.
      const reducao = tela.usaPontuacaoCliente ? 0 : 4 * Number(item.desconto_digitado || 0);
      const fatorPontos = item.ignorar_pontuacao_por_tabela ? 0 : Number(prazo.fator_pontuacao || 0);
      if (tela.exibirPontuacao) item.pontuacao_calculada = Math.round(Math.max(0,
        Number(item.qtde || 0) * Number(item.pontuacao || 0) * Number(item.valor_litragem || 0)
        * (1 + fatorPontos / 100) * (1 - reducao / 100)));
    });
    const totalizarCampanhas = (lista: ProdutoPromocional[]) => (lista || []).forEach(p => {
      if (p.id_campanha != null && p.produtos_vinculados?.length) {
        p.valor_total = p.produtos_vinculados.reduce((total, filho) => total + Number(filho.valor_total || 0), 0);
        p.pontuacao_calculada = p.produtos_vinculados.reduce((total, filho) => total + Number(filho.pontuacao_calculada || 0), 0);
      }
    });
    totalizarCampanhas(tela.produtosOriginal); totalizarCampanhas(tela.produtosAdicionados); totalizarCampanhas(tela.produtos);
    tela.totalizarProdutosAdicionados();
    tela.toastr.info('', 'Prazo atualizado e descontos revalidados.');
  } catch (erro) {
    if (tela.prazoPagamentoSelecionado) tela.prazoPagamentoSelecionado = { ...tela.prazoPagamentoSelecionado };
    tela.toastr.error('', (erro as Error)?.message || 'Não foi possível revalidar o prazo.');
  } finally { tela.revalidandoPrazo = false; }
}
